import { paths } from '../routes/paths';

// The five functional buckets PostHog groups every internal-app and cockpit
// event into. Keep this union and the route map below as the only place a
// bucket slug is spelled out — everything else should read the type or the
// resolver.
export type WorkstreamBucket = 'origination' | 'assessment' | 'buy-and-build' | 'vdr' | 'admin-platform';

// Never undefined: any pathname the map below does not recognise resolves
// here instead, so a PostHog dashboard filtering on `workstream` never sees a
// missing property.
export const UNCLASSIFIED_WORKSTREAM = 'unclassified';

export type Workstream = WorkstreamBucket | typeof UNCLASSIFIED_WORKSTREAM;

// companyFinder's constants are bare segments (no leading slash) because
// they were written to be nested under a parent route; the routes they
// describe are top-level, so prefix the slash back on here rather than
// changing the shared paths.ts contract for this one caller.
const SOFTWARE_CATEGORIES_PATH = `/${paths.companyFinder.softwareCategories}`;
const CUSTOMER_INDUSTRIES_PATH = `/${paths.companyFinder.customerIndustries}`;
const MATRIX_SEARCH_PATH = `/${paths.companyFinder.matrixSearch}`;

// Registered outside paths.ts: it is a top-level route wired directly in
// routes/index.tsx rather than through the shared paths object.
const ACCESS_DENIED_PATH = '/access-denied';

// The external Seller Portal tree, mounted at /seller like the cockpit at
// /cockpit (App.tsx branch) — not in paths.ts because it is a separate tree.
const SELLER_PORTAL_PATH = '/seller';

// Route -> bucket. Order does not matter: resolveWorkstream always picks the
// entry whose static path segments are the longest match for the current
// pathname, so a more specific child route always wins over a shorter parent
// even when both appear here (e.g. the buy-and-build entries under
// /evaluation win over the /evaluation entry itself).
const WORKSTREAM_ROUTES: ReadonlyArray<readonly [string, WorkstreamBucket]> = [
  // Bucket 1: origination
  [paths.search, 'origination'],
  [paths.pageSearch, 'origination'],
  [SOFTWARE_CATEGORIES_PATH, 'origination'],
  [CUSTOMER_INDUSTRIES_PATH, 'origination'],
  [MATRIX_SEARCH_PATH, 'origination'],
  [paths.crawlerInbox, 'origination'],
  [paths.transactions, 'origination'],
  [paths.transactionsInvestors, 'origination'],
  [paths.investorsGraph, 'origination'],
  [paths.dealAnnouncements, 'origination'],
  [paths.evaluation.outreach, 'origination'],
  [paths.evaluation.startups, 'origination'],
  [paths.validation, 'origination'],
  [paths.validationV2, 'origination'],
  [paths.dataQuality.main, 'origination'],
  [paths.dataQuality.review, 'origination'],
  [paths.statistics, 'origination'],
  [paths.survey, 'origination'],
  // BID's own CRM deal funnel. It reported 'buy-and-build' until the board
  // moved out of the cockpit tree, but only because it sat under /cockpit and
  // matched that prefix -- paths.ts called it the internal CRM deal funnel even
  // then. Its lanes are `direct` / `advisor-led`, i.e. how a deal reached BID,
  // which is origination; the PortCo add-on vocabulary the buy-and-build bucket
  // describes lives on the Pipeline board instead (see DEAL_LANES in
  // pages/DealFunnel/dealTypes.ts). Moving it here steps the buy-and-build
  // series down at the deploy: that is the correction, not a regression.
  [paths.crmDealFunnel, 'origination'],

  // Bucket 2: assessment
  [paths.evaluation.evaluation, 'assessment'],
  [paths.evaluation.company, 'assessment'],
  [paths.evaluation.market, 'assessment'],
  [paths.evaluation.dueDiligence, 'assessment'],
  [paths.evaluation.dfmStandalone, 'assessment'],
  [paths.evaluation.techAssessment, 'assessment'],
  [paths.evaluation.companyFiles, 'assessment'],
  [paths.evaluation.companyMeetings, 'assessment'],
  [paths.evaluation.companyAnnotations, 'assessment'],
  [paths.evaluation.marketResearch, 'assessment'],
  [paths.evaluation.marketSegments, 'assessment'],
  [paths.evaluation.aiThreatIndex, 'assessment'],
  [paths.benchmarking, 'assessment'],
  [paths.industryBenchmarks, 'assessment'],
  [paths.research.industryBenchmarks, 'assessment'],
  [paths.research.valuationModel, 'assessment'],
  [paths.research.industryCodePilot, 'assessment'],
  [paths.aiUpload, 'assessment'],
  [paths.meetings, 'assessment'],
  [paths.evaluation.customerSegment, 'assessment'],

  // Bucket 3: buy-and-build. Only the cockpit tree's root is listed: it is a
  // separate React tree mounted at /cockpit (see routes/paths.ts), so every
  // /cockpit/* pathname reaches this resolver as a full absolute path and
  // matches here via the longest-prefix rule — there is no other bucket
  // whose route shares the /cockpit prefix to compete with it.
  [paths.cockpit, 'buy-and-build'],
  [paths.evaluation.bbLists, 'buy-and-build'],
  [paths.evaluation.dealScout, 'buy-and-build'],

  // Bucket 4: vdr — the seller process end to end: the external portal
  // tree, the internal Execution flow, and the two admin fallback surfaces.
  [SELLER_PORTAL_PATH, 'vdr'],
  [paths.evaluation.execution, 'vdr'],
  [paths.admin.vdr, 'vdr'],
  [paths.admin.sellerPortal, 'vdr'],

  // Bucket 5: admin-platform
  [paths.settings, 'admin-platform'],
  [paths.dealAssessmentTemplate, 'admin-platform'],
  [paths.aiThreatDesign, 'admin-platform'],
  [paths.scheduledTasks, 'admin-platform'],
  [paths.appInsight, 'admin-platform'],
  [paths.meetingTemplates, 'admin-platform'],
  [paths.featureToggles, 'admin-platform'],
  [paths.fileConventionRules, 'admin-platform'],
  [paths.dealLostReasons, 'admin-platform'],
  [paths.dealStages, 'admin-platform'],
  [paths.admin.dataEditor, 'admin-platform'],
  [paths.itSupport, 'admin-platform'],
  [paths.notifications, 'admin-platform'],
  [paths.pipedriveActivity, 'admin-platform'],
  [paths.gdriveRelink, 'admin-platform'],
  [paths.main, 'admin-platform'],
  [ACCESS_DENIED_PATH, 'admin-platform'],
  [paths.crawlerOps.main, 'admin-platform'],
  [paths.crawlerOps.pipedrive, 'admin-platform'],
  [paths.crawlerOps.mlStats, 'admin-platform'],
  [paths.crawlerOps.signalTracking, 'admin-platform'],
  [paths.crawlerOps.userActivity, 'admin-platform'],
  [paths.crawlerOps.dashboard, 'admin-platform'],
  [paths.crawlerOps.enrichmentPerformance, 'admin-platform'],
  [paths.crawlerOps.swClassifier, 'admin-platform'],
  [paths.crawlerOps.crawlQueue, 'admin-platform'],

  // /admin/questionnaires, /chat, and any evidio.ai tenant route are
  // deliberately absent — their bucket is still undecided, so they resolve
  // to UNCLASSIFIED_WORKSTREAM until the map is updated with a decision.
];

function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

// A route pattern's static prefix is everything before its first
// react-router param placeholder (a segment starting with ":"). That is
// enough to disambiguate every entry in WORKSTREAM_ROUTES today: no two
// entries share a static prefix and diverge only after a param segment.
function staticPrefixSegments(routePath: string): string[] {
  const segments = pathSegments(routePath);
  const paramIndex = segments.findIndex(segment => segment.startsWith(':'));
  return paramIndex === -1 ? segments : segments.slice(0, paramIndex);
}

function isPrefixOf(prefix: string[], candidate: string[]): boolean {
  if (prefix.length > candidate.length) return false;
  return prefix.every((segment, index) => segment === candidate[index]);
}

/**
 * Resolves a React Router pathname to the workstream bucket PostHog should
 * tag its events with. Matching is longest-prefix / parent-wins: among every
 * route whose static segments prefix the pathname, the one with the most
 * segments decides the bucket, so a specific child route always beats a
 * shorter parent that also matches. Never returns undefined — an unmapped
 * pathname resolves to UNCLASSIFIED_WORKSTREAM.
 */
export function resolveWorkstream(pathname: string): Workstream {
  const candidate = pathSegments(pathname);

  let best: { length: number; bucket: WorkstreamBucket } | null = null;
  for (const [routePath, bucket] of WORKSTREAM_ROUTES) {
    const prefix = staticPrefixSegments(routePath);
    // An empty prefix (the root path "/") only matches the root pathname
    // itself — otherwise every pathname would vacuously match it.
    if (prefix.length === 0 ? candidate.length !== 0 : !isPrefixOf(prefix, candidate)) continue;
    if (!best || prefix.length > best.length) {
      best = { length: prefix.length, bucket };
    }
  }

  return best?.bucket ?? UNCLASSIFIED_WORKSTREAM;
}
